import { makeStyles } from '@ellucian/react-design-system/core';
import {
    spacing30,
    spacing20,
    spacing10,
    colorTextNeutral200,
    colorTextNeutral600,
    colorGlobalBorderDefault,
    borderRadiusSmall,
    borderWidthThin,
    borderWidthThick
} from '@ellucian/react-design-system/core/styles/tokens';
import { formatFileSizeCompact, formatUploadDate } from '../../../utilities/fileHelpers';
import { useIntl } from 'react-intl';
import { Typography, IconButton, Tooltip, CircularProgress } from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';

const useStyles = makeStyles({
    name: 'FileCard'
})(() => ({
    fileCard: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing30,
        padding: spacing30,
        backgroundColor: colorTextNeutral200,
        borderRadius: borderRadiusSmall,
        border: `1px solid ${colorGlobalBorderDefault}` },
    fileIcon: {
        flexShrink: 0,
        width: '24px',
        height: '24px' },
    loadingIcon: {
        flexShrink: 0,
        width: '24px',
        height: '24px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center' },
    progressContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px' },
    fileInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing20 },
    fileName: {
        fontWeight: 400 },
    fileMetadata: {
        color: colorTextNeutral600 },
    deleteButtonUnboxed: {
        padding: spacing10,
        minWidth: 'auto',
        minHeight: 'auto',
        border: 'none',
        borderRadius: spacing10,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:focus-visible': {
            outline: `${borderWidthThick || borderWidthThin} solid currentColor`,
            outlineOffset: spacing20
        }
    },
    actionsContainer: {
        display: 'flex',
        gap: spacing20,
        alignItems: 'center' } }));

const FileCard = ({ file, locale, onDelete, onView, isGroupUploading = false }) => {
    const { classes } = useStyles();
    const intl = useIntl();
    const uploadDate = formatUploadDate(file.uploadedAt, locale);
    const displayName = file.name || file.originalName || file.documentId || '-';
    const deleteText = intl.formatMessage({ id: 'delete' });
    const viewText = intl.formatMessage({ id: 'supportingDocumentsView' });
    const deleteLabel = `${deleteText} ${displayName}`;
    const viewLabel = `${viewText} ${displayName}`;
    const isUploading = file.status === 'uploading';

    return (
        <div className={classes.fileCard} role="listitem">
            <span className={isUploading ? classes.progressContainer : classes.fileIcon} aria-hidden="true">
                {isUploading ? (
                    <CircularProgress 
                        size={24} 
                        variant="indeterminate"
                        aria-label={intl.formatMessage({ id: 'uploadDialogStatusUploading' })}
                    />
                ) : (
                    <Icon name="file-text" />
                )}
            </span>
            <div className={classes.fileInfo}>
                <Typography className={classes.fileName}>
                    {displayName}
                </Typography>
                <Typography className={classes.fileMetadata} aria-label={`${formatFileSizeCompact(file.size)}, uploaded ${uploadDate}`}>
                    {`${formatFileSizeCompact(file.size)} • ${uploadDate}`}
                </Typography>
            </div>
            {(onView || onDelete) && !isUploading && !isGroupUploading && (
                <div className={classes.actionsContainer} role="group" aria-label={intl.formatMessage({ id: 'filteredSearchDialogActionsRow' })} style={{ display: 'flex', gap: spacing20, alignItems: 'center' }}>
                    {onView && (
                        <Tooltip title={viewText} arrow placement="top">
                            <IconButton
                                color="gray"
                                onClick={onView}
                                aria-label={viewLabel}
                                className={classes.deleteButtonUnboxed}
                            >
                                <Icon name="show" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Tooltip title={deleteText} arrow placement="top">
                            <IconButton
                                color="gray"
                                onClick={onDelete}
                                aria-label={deleteLabel}
                                className={classes.deleteButtonUnboxed}
                            >
                                <Icon name="trash" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};

export default FileCard;
